'use client';

import { forwardRef, useMemo } from 'react';
import type { ComponentProps } from 'react';

import { useWorkspaceSlug } from '@/components/dashboard/workspace-context';
import { withWorkspace, withoutWorkspace } from '@/lib/workspace-route';
import { IntlLink, useIntlPathname, useIntlRouter } from './intl-navigation';

/**
 * ÇALIŞMA ALANI FARKINDA GEZİNME.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden sarmalayıcı — 196 adresi elle yazmak yerine
 * ═══════════════════════════════════════════════════════════════════════════
 * Etkin alan adrese girdi (`/dashboard/w/<slug>/…`). Panelde `/dashboard`
 * ile başlayan 196 adres var, 80 dosyaya dağılmış. Hepsini elle çalışma
 * alanlı hâle getirmek iki şeyi garanti ederdi: birkaçının unutulması ve
 * unutulanların SESSİZ bozulması — alansız adres seçim eşiğine düşer, oradan
 * "son kullanılan" alana gider, yani ÇOĞU ZAMAN doğru görünür ve yalnız
 * ikinci sekmede yanlış kiracıyı açardı.
 *
 * next-intl locale önekini tam olarak bu gerekçeyle merkezî olarak ekliyor.
 * Buradaki katman aynı hilenin ikincisi: çağrı yerleri `/dashboard/campaigns`
 * yazmaya devam ediyor, segment tek yerde ekleniyor.
 *
 * ---------------------------------------------------------------------------
 * `usePathname` ters yönde çalışıyor
 * ---------------------------------------------------------------------------
 * Sol menü "hangi bağlantı etkin" sorusunu `pathname.startsWith('/dashboard/campaigns')`
 * ile cevaplıyor. Ham adres `/dashboard/w/blesyum/campaigns` olduğu için
 * hiçbir eşleşme tutmazdı ve menüde HİÇBİR öğe seçili görünmezdi. Bu yüzden
 * okuma yönünde segment SÖKÜLÜYOR: dışarıya hep alansız adres veriliyor,
 * içeriye hep alanlı yazılıyor.
 *
 * ---------------------------------------------------------------------------
 * Bağlam yoksa hiçbir şey olmuyor
 * ---------------------------------------------------------------------------
 * `useWorkspaceSlug()` panel kabuğu dışında `null` dönüyor (giriş ekranı,
 * pazarlama sayfaları, `/admin`). O durumda adres olduğu gibi geçiyor —
 * sarmalayıcı her yerde güvenle kullanılabiliyor.
 */

type IntlLinkProps = ComponentProps<typeof IntlLink>;

/**
 * `href` yalnız DİZE ise dönüştürülüyor.
 *
 * next-intl nesne biçimini de kabul ediyor (`{ pathname, params }`) ama bu
 * kod tabanında tek bir kullanımı yok; nesneyi de dönüştürmek, hiç
 * çalıştırılmayan bir dal yazmak olurdu. Nesne geldiği gün tip hatası
 * vermiyor, sessizce alansız gidiyor — bu yüzden aşağıdaki dal açıkça
 * yazılı ve bir gün gerekirse buradan genişletilir.
 */
function resolveHref(href: IntlLinkProps['href'], slug: string | null): IntlLinkProps['href'] {
  return typeof href === 'string' ? withWorkspace(href, slug) : href;
}

export const Link = forwardRef<HTMLAnchorElement, IntlLinkProps>(function Link(props, ref) {
  const slug = useWorkspaceSlug();
  const { href, ...rest } = props;

  return <IntlLink ref={ref} href={resolveHref(href, slug)} {...rest} />;
});

/**
 * `push`/`replace`/`prefetch` alan segmentini kendiliğinden ekliyor.
 *
 * `back`/`forward` olduğu gibi geçiyor — tarayıcı geçmişi zaten tam adresi
 * tutuyor ve ona dokunmak geri tuşunu bozardı.
 */
export function useRouter() {
  const router = useIntlRouter();
  const slug = useWorkspaceSlug();

  return useMemo(
    () => ({
      ...router,
      push: (href: string, options?: Parameters<typeof router.push>[1]) =>
        router.push(withWorkspace(href, slug), options),
      replace: (href: string, options?: Parameters<typeof router.replace>[1]) =>
        router.replace(withWorkspace(href, slug), options),
      prefetch: (href: string, options?: Parameters<typeof router.prefetch>[1]) =>
        router.prefetch(withWorkspace(href, slug), options),
    }),
    [router, slug],
  );
}

/** Alan segmenti SÖKÜLMÜŞ adres — çağıranlar `/dashboard/…` görmeye devam eder. */
export function usePathname(): string {
  return withoutWorkspace(useIntlPathname());
}
